// `LeadDetailDrawer` — US-143, US-144, US-147.
//
// Wraps the DS-D `LeadDetailPanel` composite and binds the action handlers to
// `useUpdateLead` / `useDeleteLead` / navigation. Mounted inline by the
// `LeadsTablePage` (the global drawer primitive's static body slot is too
// shallow for the multi-section panel — instead we instantiate the composite
// itself which owns its own DS `Drawer` root).

import { useNavigate } from '@tanstack/react-router';

import { useT } from '@barel/i18n';
import { Button, LeadDetailPanel, toast } from '@barel/ui';
import {
  useConversation,
  useDeleteLead,
  useReopenConversation,
  useUnarchiveSingleLead,
  type LeadRow,
} from '@barel/api-client/react';

import {
  formatLastActivity,
  toDetailData,
  type AccountLite,
  type BusinessLite,
} from './lib/leadAdapters';
import { LeadTagsEditor } from './LeadTagsEditor';
import { StatusInlineEditor } from './StatusInlineEditor';

export interface LeadDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: LeadRow | null;
  accounts: AccountLite[];
  businesses: BusinessLite[];
  /** Triggered when the user clicks "Send to CRM" — opens the CRM modal. */
  onSendToCrm: (lead: LeadRow) => void;
}

export function LeadDetailDrawer({
  open,
  onOpenChange,
  lead,
  accounts,
  businesses,
  onSendToCrm,
}: LeadDetailDrawerProps) {
  const t = useT('leads');
  const navigate = useNavigate();
  const deleteLead = useDeleteLead();
  const unarchive = useUnarchiveSingleLead();
  const reopen = useReopenConversation();
  // Pull the linked conversation when the lead has one — used by the
  // "Reopen conversation" button to gate visibility on `outcomeStatus`.
  const convId = lead?.conversationId ?? undefined;
  const convQ = useConversation(convId, { enabled: open && Boolean(convId) });

  if (!lead) return null;

  const account = accounts.find((a) => a.id === lead.linkedinAccountId);
  const business = businesses.find((b) => b.id === lead.businessId);
  const lastActivity = formatLastActivity(
    lead.lastActivityAt,
    'fr-FR',
    t('detail.today'),
    t('detail.yesterday'),
  );
  const data = toDetailData(lead, account, business, lastActivity.iso);

  const validatedSentence = t(`detail.validatedFrom.${lead.validatedFrom}`);

  return (
    <LeadDetailPanel
      open={open}
      onOpenChange={onOpenChange}
      lead={data}
      labels={{
        sourceEyebrow: t('detail.sourceEyebrow'),
        validatedFrom: validatedSentence,
        accountCaption: t('detail.accountCaption'),
        businessCaption: t('detail.businessCaption'),
        lastActivityCaption: t('detail.lastActivityCaption'),
        lastActivityValue: lastActivity.label,
        validatedCaption: t('detail.validatedCaption'),
        status: t(`status.${lead.status}`),
        source: t(`source.${lead.sourcePill.toLowerCase()}`),
        lastActionHeader: t('detail.lastActionHeader'),
        crmSyncedLabel:
          lead.crmSync && lead.crmTarget
            ? t('detail.crmSyncedLabel', {
                target: t(`crm.targets.${lead.crmTarget}.label`),
              })
            : undefined,
        openConvLabel: t('detail.openConv'),
        sendToCrmLabel: t('detail.sendToCrm'),
        archiveLabel: t('detail.archive'),
        closeLabel: t('detail.close'),
      }}
      onOpenConversation={(convId) => {
        // US-144: cross-tab handoff via `?selectedId=…` URL state.
        onOpenChange(false);
        void navigate({
          to: '/inbox',
          search: { selectedId: convId, accountId: undefined, mode: undefined },
        });
      }}
      onSendToCrm={() => onSendToCrm(lead)}
      onArchive={() => {
        const archivedId = lead.id;
        deleteLead.mutate(
          { id: archivedId },
          {
            onSuccess: () => {
              // Wave C1 (US-147) — 5-second undo. Toast `action` re-runs
              // unarchive against the snapshotted id so the archive is
              // reversible without a refresh.
              toast.success({
                title: t('toast.archived'),
                duration: 5000,
                action: {
                  label: t('toast.undo'),
                  onClick: () => {
                    unarchive.mutate(
                      { id: archivedId },
                      {
                        onSuccess: () => {
                          toast.info({ title: t('toast.unarchived') });
                        },
                        onError: () => {
                          toast.error({ title: t('toast.archiveError') });
                        },
                      },
                    );
                  },
                },
              });
              onOpenChange(false);
            },
            onError: () => {
              toast.error({ title: t('toast.archiveError') });
            },
          },
        );
      }}
      footerSlot={
        <div className="mt-3 flex flex-col gap-2" data-testid="lead-detail-status-editor">
          <LeadTagsEditor leadId={lead.id} enabled={open} />
          {/* Wave C1 (US-067) — Reopen conversation button. Visible only when
              the linked conversation reports a non-null outcome (i.e. the
              user previously archived/won it). */}
          {convId && convQ.data?.conversation.outcomeStatus ? (
            <Button
              variant="ghost"
              size="sm"
              data-testid="lead-detail-reopen"
              title={t('detail.reopen_conversation_tooltip')}
              disabled={reopen.isPending}
              onClick={() => {
                reopen.mutate(
                  { id: convId },
                  {
                    onSuccess: () => {
                      toast.success({ title: t('toast.reopened') });
                      onOpenChange(false);
                      void navigate({
                        to: '/inbox',
                        search: {
                          selectedId: convId,
                          accountId: undefined,
                          mode: undefined,
                        },
                      });
                    },
                    onError: () => {
                      toast.error({ title: t('toast.reopenError') });
                    },
                  },
                );
              }}
            >
              {t('detail.reopen_conversation_button')}
            </Button>
          ) : null}
          <div>
            <span className="mr-2 text-xs text-tertiary">{t('detail.statusInline')}</span>
            <StatusInlineEditor lead={lead} />
          </div>
        </div>
      }
    />
  );
}
